import { cloneDeep } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { succesToast, errorToast } from 'basics/utils/toast';
import { Tactic } from 'generated/maya.types';
import { PAGINATION_PAGE_SIZE } from 'modules/maya/basics/constants/common.constant';
import { CepStatus, TacticMode, TacticStatus } from 'modules/maya/basics/enums/maya.enums';
import { CepStateType } from 'modules/maya/basics/types/maya.types';
import useTacticListConfig from 'modules/maya/components/CepForm/Steps/TacticsStep/TacticsList/TacticsList.config';
import { useSetTacticStatusToDeletedMutation } from 'modules/maya/graphql/mutations/setTacticStatusToDeleted';
import useGetAllTacticsQuery from 'modules/maya/graphql/queries/getAllTactics';
import cepState from 'modules/maya/states/cep.states';
import tacticState from 'modules/maya/states/tactic.states';

const useTacticsList = () => {
  const { t } = useTranslation();

  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const cepId = cepStateValue.cep?._id;
  const isCepApproved = cepStateValue.cep?.status === CepStatus.APPROVED;

  const [setTacticStatusToDeleted, { loading: removeTacticLoading }] = useSetTacticStatusToDeletedMutation();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGINATION_PAGE_SIZE);
  const [rowCountState, setRowCountState] = useState(0);
  const [open, setOpen] = useState(false);
  const [rowId, setRowId] = useState('');
  const [tactics, setTactics] = useState<Tactic[]>([]);

  const queryOptions = useMemo(
    () => ({
      limit: pageSize,
      offset: page * pageSize,
    }),
    [page, pageSize],
  );

  const queryFilters = useMemo(
    () => ({
      cep: cepId,
      status: [TacticStatus.DRAFT, TacticStatus.APPROVED],
    }),
    [cepId],
  );

  const { data, loading, count } = useGetAllTacticsQuery(queryOptions, queryFilters);

  useEffect(() => {
    setRowCountState((prevRowCountState) => (count !== undefined ? count : prevRowCountState));
  }, [count, setRowCountState]);

  useEffect(() => {
    if (!loading) {
      setTactics(data);
    }
  }, [data, loading]);

  const newTacticClick = () => {
    tacticState.mode = TacticMode.form;
  };

  const editTacticClick = (id: string) => {
    const currentTactic = data?.find((tactic) => tactic._id === id);
    if (currentTactic) {
      tacticState.tactic = currentTactic;
      tacticState.mode = TacticMode.form;
    }
  };

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const onPageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const newElementButton = {
    newElementLabel: t('maya_list_new_element_label', { elementTitle: 'Tactic' }),
    newElementClick: newTacticClick,
    newElementDisabled: isCepApproved,
  };

  const deleteTacticCallback = async (tacticId: string) => {
    const isTacticDeleted = await setTacticStatusToDeleted(tacticId);

    if (isTacticDeleted.data) {
      succesToast(t('maya_action_deleting_tactic_success'));
      const clonedTactics = cloneDeep(tactics);
      const targetTacticsIndex = clonedTactics.findIndex((tactic) => { return tactic._id === tacticId; });
      if (targetTacticsIndex !== -1) {
        clonedTactics.splice(targetTacticsIndex, 1);
        setTactics(clonedTactics);
      }
    } else if (isTacticDeleted.error) {
      errorToast(isTacticDeleted.error);
    }
    setOpen(false);
  };

  const openModalCallback = (tacticId: string) => {
    setOpen(true);
    setRowId(tacticId);
  };

  const { tacticListColumnConfig } = useTacticListConfig(editTacticClick, openModalCallback);

  return {
    deleteTacticCallback,
    editTacticClick,
    loading,
    newElementButton,
    onPageChange,
    onPageSizeChange,
    open,
    openModalCallback,
    removeTacticLoading,
    rowCountState,
    rowId,
    setOpen,
    tacticListColumnConfig,
    tactics,
  };
};

export default useTacticsList;
